import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  ThemeProvider,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Box,
  Breadcrumbs,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { createTheme } from "@mui/material/styles";
import InsightsIcon from "@mui/icons-material/Insights";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { getTreatments } from "../API/treatmentAPI";
import { getClient } from "../API/clientAPI";
import { getClientAddressById } from "../API/clientAddressAPI";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

function LeadList() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    // Format: dd-MMM-yyyy hh:mm:ss a
    const formattedDate = format(date, "dd-MMM-yyyy hh:mm:ss a");

    return formattedDate;
  };

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const treatmentsData = await getTreatments([
          "open",
          "closed",
          "ocular",
        ]);

        if (Array.isArray(treatmentsData)) {
          const formattedLeads = await Promise.all(
            treatmentsData.map(async (treatment) => {
              const client = await getClient(treatment.clientId);
              const address = await getClientAddressById(
                treatment.clientAddressId
              );
              const combinedDateTime = new Date(
                `${treatment.date}T${treatment.time}`
              );
              return {
                ...treatment,
                client,
                address,
                combinedDateTime: formatDate(combinedDateTime),
                formattedEntryDate: formatDate(treatment.timestamp_create),
              };
            })
          );

          setLeads(formattedLeads);
          setLoading(false);
        }
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchLeads();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredLeads = leads.filter((lead) =>
    [
      lead.client.name,
      lead.address[0]?.address,
      lead.description,
      lead.type,
      lead.status,
    ].some((value) =>
      value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ padding: "40px 60px" }}>
        <Card
          variant="outlined"
          style={{
            marginTop: "40px",
            marginBottom: "40px",
            borderRadius: 0,
          }}
        >
          <CardHeader
            title={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center">
                  <InsightsIcon
                    sx={{
                      fontSize: "40px",
                      marginRight: "8px",
                    }}
                  />
                  <Typography variant="h6">Sales</Typography>
                </Box>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<NavigateNextIcon fontSize="small" />}
                  sx={{ color: "white" }}
                >
                  <Typography sx={{ color: "#ffffff99" }}>Sales</Typography>
                  <Typography
                    component={Link}
                    to="/list-leads"
                    sx={{ color: "#ffffff", textDecoration: "none" }}
                  >
                    Leads
                  </Typography>
                </Breadcrumbs>
              </Box>
            }
            sx={{
              backgroundColor: "#1e5d45",
              color: "white",
              padding: "16px",
            }}
          />

          <CardContent sx={{ padding: "20px" }}>
            <Card variant="outlined" sx={{ padding: "20px" }}>
              <CardHeader
                title={
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    Leads
                  </Typography>
                }
                action={
                  <Button
                    component={Link}
                    to="/create-lead"
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                  >
                    Create Lead
                  </Button>
                }
                sx={{ marginBottom: 2 }}
              />
              <TextField
                variant="outlined"
                placeholder="Search by client name, address, treatment, type, or status..."
                value={searchQuery}
                onChange={handleSearchChange}
                fullWidth
                sx={{ marginBottom: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 2,
                    paddingBottom: "20px",
                  }}
                >
                  <CircularProgress />
                  <Typography variant="body1" sx={{ marginLeft: 2 }}>
                    Loading leads...
                  </Typography>
                </Box>
              ) : error ? (
                <Typography variant="body1" color="error" sx={{ padding: 2 }}>
                  Error fetching leads: {error}
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Client
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Address
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Treatment
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Type
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Status
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Treatment Date
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Entry Date
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                              textAlign: "center",
                            }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredLeads.length > 0 ? (
                          filteredLeads
                            .slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage
                            )
                            .map((lead, index) => (
                              <TableRow key={index}>
                                <TableCell>{lead.client.name}</TableCell>
                                <TableCell>
                                  {lead.address[0]?.address}
                                </TableCell>
                                <TableCell>{lead.description}</TableCell>
                                <TableCell>{lead.type}</TableCell>
                                <TableCell>{lead.status}</TableCell>
                                <TableCell>{lead.combinedDateTime}</TableCell>
                                <TableCell>{lead.formattedEntryDate}</TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Tooltip title="View">
                                      <IconButton
                                        component={Link}
                                        to={`/view-lead/${lead.id}`}
                                      >
                                        <VisibilityIcon
                                          sx={{
                                            fontSize: "1.2rem",
                                            color: "#6C757D",
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Update">
                                      <IconButton
                                        component={Link}
                                        to={`/edit-lead/${lead.id}`}
                                      >
                                        <EditIcon
                                          sx={{
                                            fontSize: "1.2rem",
                                            color: "#007BFF",
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        component={Link}
                                        to={`/delete-lead/${lead.id}`}
                                      >
                                        <DeleteIcon
                                          sx={{
                                            fontSize: "1.2rem",
                                            color: "#DC3545",
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              No leads found.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={filteredLeads.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableContainer>
                </>
              )}
            </Card>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
}

export default LeadList;
